/**
 * PlanRestrictedButton - A button that shows restrictions based on user's plan
 * Displays disabled state with tooltip when feature is not available
 */

import React, { useState, useRef, useEffect, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Crown, Zap, ArrowUpRight } from 'lucide-react';
import { usePlanRestrictions, PLAN_NAMES } from '../hooks/usePlanRestrictions';

/**
 * Tooltip component for restriction messages
 */
const RestrictionTooltip = ({ children, message, show, position = 'top', onUpgrade }) => {
  const tooltipRef = useRef(null);
  const [tooltipStyle, setTooltipStyle] = useState({});

  useEffect(() => {
    if (show && tooltipRef.current) {
      const tooltip = tooltipRef.current;
      const rect = tooltip.getBoundingClientRect();

      // Adjust position if tooltip goes off-screen
      let style = {};
      if (position === 'top' && rect.top < 0) {
        style.top = '100%';
        style.bottom = 'auto';
        style.marginTop = '8px';
        style.marginBottom = '0';
      }
      if (rect.right > window.innerWidth) {
        style.left = 'auto';
        style.right = '0';
        style.transform = 'none';
      }
      if (rect.left < 0) {
        style.left = '0';
        style.transform = 'none';
      }
      setTooltipStyle(style);
    }
  }, [show, position]);

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2'
  };

  const arrowClasses = {
    top: 'top-full left-1/2 -translate-x-1/2 border-t-gray-800 border-l-transparent border-r-transparent border-b-transparent',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 border-b-gray-800 border-l-transparent border-r-transparent border-t-transparent',
    left: 'left-full top-1/2 -translate-y-1/2 border-l-gray-800 border-t-transparent border-b-transparent border-r-transparent',
    right: 'right-full top-1/2 -translate-y-1/2 border-r-gray-800 border-t-transparent border-b-transparent border-l-transparent'
  };

  return (
    <div className="relative inline-flex">
      {children}
      {show && (
        <div
          ref={tooltipRef}
          className={`absolute z-50 ${positionClasses[position]}`}
          style={tooltipStyle}
        >
          <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-xl p-3 min-w-[200px] max-w-[280px]">
            <div className="flex items-start gap-2">
              <Lock className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-white font-medium mb-1">Restricted feature</p>
                <p className="text-xs text-gray-400 mb-2">{message}</p>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onUpgrade?.();
                  }}
                  className="flex items-center gap-1 text-xs text-brand-green hover:text-brand-green/80 font-medium transition-colors"
                >
                  <Crown className="w-3 h-3" />
                  Changer de plan
                  <ArrowUpRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
          {/* Arrow */}
          <div className={`absolute w-0 h-0 border-[6px] ${arrowClasses[position]}`} />
        </div>
      )}
    </div>
  );
};

/**
 * PlanRestrictedButton Component
 *
 * @param {Object} props
 * @param {string} props.feature - Feature name to check (from FEATURE_REQUIREMENTS)
 * @param {string} props.requiredPlan - Direct plan requirement (alternative to feature)
 * @param {string} props.quotaType - Quota to check (e.g., 'workspaces', 'scans_per_month')
 * @param {number} props.creditsNeeded - Credits required for this action
 * @param {Function} props.onClick - Click handler when not restricted
 * @param {string} props.className - Additional CSS classes
 * @param {React.ReactNode} props.children - Button content
 * @param {string} props.tooltipPosition - Tooltip position (top, bottom, left, right)
 * @param {boolean} props.showLockIcon - Show lock icon when restricted
 * @param {string} props.disabledClassName - CSS classes for disabled state
 * @param {Object} props.style - Inline styles
 * @param {string} props.as - Element type ('button', 'a', 'div')
 * @param {string} props.href - Link href (when as='a')
 * @param {boolean} props.disabled - External disabled state
 */
const PlanRestrictedButton = forwardRef(({
  feature,
  requiredPlan,
  quotaType,
  creditsNeeded,
  onClick,
  className = '',
  children,
  tooltipPosition = 'top',
  showLockIcon = true,
  disabledClassName = '',
  style = {},
  as: Component = 'button',
  href,
  disabled: externalDisabled = false,
  ...props
}, ref) => {
  const navigate = useNavigate();
  const [showTooltip, setShowTooltip] = useState(false);
  const {
    hasPlanOrHigher,
    hasFeature,
    isWithinQuota,
    creditSummary,
    currentPlan,
    getRestrictionMessage,
    checkRestriction
  } = usePlanRestrictions();

  // Determine if restricted
  let isRestricted = false;
  let restrictionMessage = '';

  // Check feature requirement
  if (feature && !hasFeature(feature)) {
    isRestricted = true;
    const restriction = checkRestriction(feature);
    restrictionMessage = getRestrictionMessage(restriction);
  }

  // Check direct plan requirement
  if (requiredPlan && !hasPlanOrHigher(requiredPlan)) {
    isRestricted = true;
    restrictionMessage = `Cette fonctionnalite necessite le ${PLAN_NAMES[requiredPlan] || requiredPlan}`;
  }

  // Check quota
  if (quotaType && !isWithinQuota(quotaType)) {
    isRestricted = true;
    restrictionMessage = `Quota atteint pour ${quotaType.replace(/_/g, ' ')}`;
  }

  // Check credits
  if (creditsNeeded && creditSummary) {
    const availableCredits = creditSummary.total_available || 0;
    if (availableCredits < creditsNeeded) {
      isRestricted = true;
      restrictionMessage = `Credits insuffisants (${creditsNeeded} requis, ${availableCredits} disponibles)`;
    }
  }

  const isDisabled = isRestricted || externalDisabled;

  const handleClick = (e) => {
    if (isRestricted) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onClick?.(e);
  };

  const handleUpgrade = () => {
    navigate('/pricing');
  };

  // Base disabled styles
  const disabledStyles = isDisabled
    ? `opacity-50 cursor-not-allowed ${disabledClassName || 'bg-gray-700 hover:bg-gray-700 text-gray-400'}`
    : '';

  const buttonContent = (
    <>
      {showLockIcon && isRestricted && (
        <Lock className="w-4 h-4 mr-1 text-yellow-400" />
      )}
      {children}
    </>
  );

  const commonProps = {
    ref,
    className: `${className} ${disabledStyles}`.trim(),
    style,
    onMouseEnter: () => isRestricted && setShowTooltip(true),
    onMouseLeave: () => setShowTooltip(false),
    onFocus: () => isRestricted && setShowTooltip(true),
    onBlur: () => setShowTooltip(false),
    'aria-disabled': isDisabled,
    ...props
  };

  // Render based on component type
  let element;
  if (Component === 'a') {
    element = (
      <a
        {...commonProps}
        href={isRestricted ? undefined : href}
        onClick={handleClick}
      >
        {buttonContent}
      </a>
    );
  } else {
    element = (
      <button
        {...commonProps}
        type={props.type || 'button'}
        disabled={isDisabled}
        onClick={handleClick}
      >
        {buttonContent}
      </button>
    );
  }

  if (isRestricted) {
    return (
      <RestrictionTooltip
        message={restrictionMessage}
        show={showTooltip}
        position={tooltipPosition}
        onUpgrade={handleUpgrade}
      >
        {element}
      </RestrictionTooltip>
    );
  }

  return element;
});

PlanRestrictedButton.displayName = 'PlanRestrictedButton';

/**
 * Higher-order component to wrap any component with plan restrictions
 */
export const withPlanRestriction = (WrappedComponent, restrictionConfig) => {
  const WithPlanRestriction = (props) => {
    const navigate = useNavigate();
    const [showTooltip, setShowTooltip] = useState(false);
    const {
      hasFeature,
      hasPlanOrHigher,
      isWithinQuota,
      creditSummary,
      getRestrictionMessage,
      checkRestriction
    } = usePlanRestrictions();

    const { feature, requiredPlan, quotaType, creditsNeeded } = restrictionConfig;

    let isRestricted = false;
    let restrictionMessage = '';

    if (feature && !hasFeature(feature)) {
      isRestricted = true;
      const restriction = checkRestriction(feature);
      restrictionMessage = getRestrictionMessage(restriction);
    }

    if (requiredPlan && !hasPlanOrHigher(requiredPlan)) {
      isRestricted = true;
      restrictionMessage = `Necessite ${PLAN_NAMES[requiredPlan]}`;
    }

    if (quotaType && !isWithinQuota(quotaType)) {
      isRestricted = true;
      restrictionMessage = `Quota ${quotaType} atteint`;
    }

    if (creditsNeeded && creditSummary && (creditSummary.total_available || 0) < creditsNeeded) {
      isRestricted = true;
      restrictionMessage = `Credits insuffisants`;
    }

    if (isRestricted) {
      return (
        <RestrictionTooltip
          message={restrictionMessage}
          show={showTooltip}
          position="top"
          onUpgrade={() => navigate('/pricing')}
        >
          <div
            className="opacity-50 cursor-not-allowed"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <WrappedComponent {...props} disabled />
          </div>
        </RestrictionTooltip>
      );
    }

    return <WrappedComponent {...props} />;
  };

  WithPlanRestriction.displayName = `WithPlanRestriction(${WrappedComponent.displayName || WrappedComponent.name})`;
  return WithPlanRestriction;
};

/**
 * Simple wrapper component for plan-restricted sections
 */
export const PlanRestrictedSection = ({
  feature,
  requiredPlan,
  quotaType,
  children,
  fallback = null,
  showOverlay = true
}) => {
  const navigate = useNavigate();
  const { hasFeature, hasPlanOrHigher, isWithinQuota } = usePlanRestrictions();

  let isRestricted = false;
  let message = '';

  if (feature && !hasFeature(feature)) {
    isRestricted = true;
    message = `Necessite une mise a niveau du plan`;
  }

  if (requiredPlan && !hasPlanOrHigher(requiredPlan)) {
    isRestricted = true;
    message = `Necessite le ${PLAN_NAMES[requiredPlan]}`;
  }

  if (quotaType && !isWithinQuota(quotaType)) {
    isRestricted = true;
    message = `Quota atteint`;
  }

  if (isRestricted) {
    if (fallback) return fallback;

    if (showOverlay) {
      return (
        <div className="relative">
          <div className="opacity-30 pointer-events-none select-none">
            {children}
          </div>
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm rounded-xl">
            <div className="text-center p-6">
              <div className="w-12 h-12 rounded-full bg-yellow-500/10 flex items-center justify-center mx-auto mb-3">
                <Lock className="w-6 h-6 text-yellow-400" />
              </div>
              <p className="text-white font-medium mb-2">{message}</p>
              <button
                onClick={() => navigate('/pricing')}
                className="flex items-center gap-2 mx-auto px-4 py-2 bg-brand-green text-black font-semibold rounded-lg hover:bg-brand-green/90 transition-all"
              >
                <Crown className="w-4 h-4" />
                Changer de plan
              </button>
            </div>
          </div>
        </div>
      );
    }

    return null;
  }

  return children;
};

export default PlanRestrictedButton;
